<template>
  <div class="transaction-success-container">
    <Banner 
      image="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?q=80&w=2070&auto=format&fit=crop" 
      title="Booking Confirmed" 
      height="700px"
    />

    <!-- Wavy Divider -->
    <div class="wave-container">
      <svg class="wave-top" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 400" preserveAspectRatio="none">
        <path class="wave-path" d="M0,96L40,106.7C80,117,160,139,240,154.7C320,171,400,181,480,165.3C560,149,640,107,720,90.7C800,75,880,85,960,112C1040,139,1120,181,1200,181.3C1280,181,1360,139,1400,117.3L1440,96L1440,400L1400,400C1360,400,1280,400,1200,400C1120,400,1040,400,960,400C880,400,800,400,720,400C640,400,560,400,480,400C400,400,320,400,240,400C160,400,80,400,40,400L0,400Z"></path>
      </svg>
    </div>

    <div class="container">
      <div class="success-card">
        <div class="success-icon">
          <i class="fas fa-check-circle"></i>
        </div>
        <h2>Thank You for Your Booking!</h2>
        <div class="booking-details">
          <div class="detail-row">
            <span class="label">Booking Reference:</span>
            <span class="value">{{ bookingReference }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Date of Visit:</span>
            <span class="value">{{ visitDate }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Total Tickets:</span>
            <span class="value">{{ totalTickets }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Total Amount:</span>
            <span class="value">SAR {{ totalAmount }}</span>
          </div>
        </div>

        <div class="ticket-info">
          <h3>What Happens Next?</h3>
          <ul>
            <li>A confirmation email has been sent to your registered email address.</li>
            <li>Your tickets are valid for the selected date.</li>
            <li>Please arrive at least 30 minutes before your scheduled time.</li>
            <li>Bring a printed or digital copy of this booking confirmation.</li>
          </ul>
        </div>

        <div class="action-buttons">
          <router-link to="/tickets" class="btn btn-secondary">
            Download Ticket
          </router-link>
          <router-link to="/" class="btn btn-primary">
            Back to Home
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import Banner from "@/components/Banner.vue";

// Generate a random booking reference
const generateBookingReference = () => {
  return `CYAN${Math.floor(100000 + Math.random() * 900000)}`;
};

const bookingReference = ref('');
const visitDate = ref('');
const totalTickets = ref(0);
const totalAmount = ref(0);

onMounted(() => {
  // Retrieve booking details from localStorage
  const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails') || '{}');
  
  bookingReference.value = generateBookingReference();
  visitDate.value = bookingDetails.date || new Date().toLocaleDateString();
  totalTickets.value = Object.values(bookingDetails.tickets || {})
    .reduce((total, ticket) => total + ticket.quantity, 0);
  totalAmount.value = bookingDetails.total || 0;
});
</script>

<style scoped>
.transaction-success-container {
  position: relative;
  background: white;
  margin-top: 700px;
}

.wave-container {
  position: absolute;
  top: -200px;
  left: 0;
  width: 100%;
  height: 220px;
  overflow: hidden;
}

.wave-top {
  position: absolute;
  bottom: -20px;
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: bottom;
  animation: waveFloat 5s ease-in-out infinite alternate;
}

.wave-path {
  fill: #ffffff;
  filter: drop-shadow(0px -2px 2px rgba(0, 0, 0, 0.1));
}

.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
}

.success-card {
  background: #f4f4f4;
  border-radius: 15px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.success-icon {
  font-size: 80px;
  color: #28a745;
  margin-bottom: 20px;
}

.success-card h2 {
  color: #333;
  margin-bottom: 30px;
}

.booking-details {
  background: white;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 30px;
  text-align: left;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f1f1f1;
}

.detail-row:last-child {
  border-bottom: none;
}

.label {
  font-weight: 600;
  color: #666;
}

.value {
  font-weight: bold;
  color: #333;
}

.ticket-info {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 30px;
  text-align: left;
}

.ticket-info h3 {
  color: #1268B1;
  margin-bottom: 15px;
}

.ticket-info ul {
  list-style-type: disc;
  padding-left: 20px;
}

.ticket-info li {
  margin-bottom: 10px;
  color: #666;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.btn {
  padding: 12px 24px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-primary {
  background-color: #1268B1;
  color: white;
}

.btn-primary:hover {
  background-color: #0d4f8c;
}

.btn-secondary {
  background-color: #EFB036;
  color: white;
}

.btn-secondary:hover {
  background-color: #cc0070;
}

@keyframes waveFloat {
  0% {
    transform: translateY(0) scaleY(1.02);
  }
  100% {
    transform: translateY(-10px) scaleY(1.05);
  }
}

@media (max-width: 768px) {
  .container {
    padding: 20px;
  }

  .success-card {
    padding: 20px;
  }

  .success-icon {
    font-size: 60px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 15px;
  }

  .wave-container {
    top: -150px;
    height: 170px;
  }
}

@media (max-width: 480px) {
  .wave-container {
    top: -100px;
    height: 120px;
  }
}

/* RTL Support */
:dir(rtl) .wave-top {
  transform: scaleX(-1);
}
</style>